import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Save, RefreshCw, Mail, Plus, Trash2, Edit3, X, CheckCircle, AlertCircle, Eye, Download, FileText, GripVertical } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { contactApi } from '../../../services/api';
import type { ContactSubmission } from '../types';

interface ContactManagerProps {
  submissions: ContactSubmission[];
  onUpdateStatus: (id: number, status: ContactSubmission['status']) => void;
  onDelete: (id: number) => void;
  onBulkDelete?: (ids: number[]) => void;
}

type SocialRow = {
  platform: string;
  url: string;
};

const normalizeContactSettings = (value: any) => ({
  ...value,
  details_badge_text: String(value?.details_badge_text || 'Contact Details'),
  details_heading: String(value?.details_heading || 'Talk To Our Team'),
  details_description: String(
    value?.details_description || 'Share your requirements through the form, or reach out directly using the details below.'
  ),
});

const parseSocialRows = (value: unknown): SocialRow[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => ({
      platform: String((item as any)?.platform || '').trim(),
      url: String((item as any)?.url || '').trim(),
    })).filter((item) => item.platform || item.url);
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parseSocialRows(parsed);
      if (parsed && typeof parsed === 'object') {
        return Object.entries(parsed).map(([platform, url]) => ({
          platform: String(platform).trim(),
          url: String(url || '').trim(),
        })).filter((item) => item.platform || item.url);
      }
    } catch {
      return [];
    }
  }
  return [];
};

const ContactManager: React.FC<ContactManagerProps> = ({ submissions, onUpdateStatus, onDelete, onBulkDelete }) => {
  const [activeTab, setActiveTab] = useState<'submissions' | 'fields' | 'settings' | 'smtp'>('submissions');
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<any | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [socialRows, setSocialRows] = useState<SocialRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailStatus, setTestEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const fieldsRef = useRef<any[]>([]);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleReorder = (newOrder: any[]) => {
    setFields(newOrder);
    
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    
    saveTimeoutRef.current = setTimeout(async () => {
      const updates = newOrder.map((field, index) => ({
        id: field.id,
        order: index
      }));
      try {
        await contactApi.reorderFields(updates);
      } catch (err) {
        console.error('Failed to save order', err);
      }
    }, 1000);
  };

  const [detail, setDetail] = useState<ContactSubmission | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'read' | 'replied' | 'archived'>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const handleCreateAtoZFields = async () => {
    if (!window.confirm('This will add a complete starter contact form field set. Continue?')) return;
    const starterFields = [
      { field_label: 'Full Name', field_key: 'name', field_type: 'text', placeholder: 'Enter your full name', is_required: true, width: 'half' },
      { field_label: 'Email Address', field_key: 'email', field_type: 'email', placeholder: 'you@company.com', is_required: true, width: 'half' },
      { field_label: 'Phone Number', field_key: 'phone', field_type: 'tel', placeholder: '+1 555 123 4567', is_required: false, width: 'half' },
      { field_label: 'Company Name', field_key: 'company_name', field_type: 'text', placeholder: 'Your company', is_required: false, width: 'half' },
      { field_label: 'Website URL', field_key: 'website_url', field_type: 'url', placeholder: 'https://example.com', is_required: false, width: 'half' },
      { field_label: 'Country', field_key: 'country', field_type: 'text', placeholder: 'Country', is_required: false, width: 'half' },
      { field_label: 'Project Type', field_key: 'project_type', field_type: 'select', options: 'Branding,Website,Web App,Mobile App,Ecommerce,3D Experience,Other', is_required: true, width: 'half' },
      { field_label: 'Budget Range', field_key: 'budget_range', field_type: 'select', options: '< $2,000,$2,000 - $5,000,$5,000 - $10,000,$10,000+', is_required: true, width: 'half' },
      { field_label: 'Expected Start Date', field_key: 'start_date', field_type: 'date', placeholder: '', is_required: false, width: 'half' },
      { field_label: 'Preferred Contact Time', field_key: 'contact_time', field_type: 'time', placeholder: '', is_required: false, width: 'half' },
      { field_label: 'Project Details', field_key: 'message', field_type: 'textarea', placeholder: 'Tell us about your project goals, timeline, and requirements...', is_required: true, width: 'full' },
      { field_label: 'I agree to the privacy policy', field_key: 'privacy_consent', field_type: 'checkbox', placeholder: '', is_required: true, width: 'full' }
    ];

    try {
      for (const field of starterFields) {
        await contactApi.addField(field);
      }
      await loadFields();
      alert('A-to-Z starter fields added successfully.');
    } catch (err) {
      console.error('Error adding starter fields:', err);
      alert('Failed to add starter fields.');
    }
  };

  useEffect(() => {
    if (activeTab === 'fields') loadFields();
    if (activeTab === 'settings' || activeTab === 'smtp') loadSettings();
  }, [activeTab]);

  const loadFields = async () => {
    try {
      const res = await contactApi.getFields();
      if (res?.success) {
        setFields(res.data);
        fieldsRef.current = res.data;
      }
    } catch (err) {
      console.error('Error loading fields:', err);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await contactApi.getSettings();
      if (res?.success) {
        setSettings(normalizeContactSettings(res.data));
        setSocialRows(parseSocialRows(res.data?.social_links));
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const handleSaveField = async () => {
    if (!editingField) return;
    try {
      if (editingField.id) {
        await contactApi.updateField(editingField.id, editingField);
      } else {
        await contactApi.addField(editingField);
      }
      setIsFieldModalOpen(false);
      loadFields();
    } catch (err) {
      console.error('Error saving field:', err);
    }
  };

  const handleDeleteField = async (id: number) => {
    if (!window.confirm('Delete this field?')) return;
    try {
      await contactApi.deleteField(id);
      loadFields();
    } catch (err) {
      console.error('Error deleting field:', err);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setIsSaving(true);
    try {
      const normalizedDisplayEmail = String(settings.email_recipient || settings.smtp_user || '').trim();
      const res = await contactApi.updateSettings({
        ...settings,
        email_recipient: normalizedDisplayEmail || null,
        smtp_user: String(settings.smtp_user || normalizedDisplayEmail || '').trim() || null,
        social_links: JSON.stringify(socialRows.filter((item) => item.platform || item.url)),
      });
      if (res?.success) {
        await loadSettings();
        alert('Contact settings saved successfully.');
      } else {
        alert(res?.message || 'Failed to save contact settings.');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save contact settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateSocialRow = (index: number, key: keyof SocialRow, value: string) => {
    setSocialRows((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const addSocialRow = () => {
    setSocialRows((prev) => [...prev, { platform: '', url: '' }]);
  };

  const removeSocialRow = (index: number) => {
    setSocialRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) return;
    setTestEmailStatus('sending');
    try {
      const res = await contactApi.testEmail({ email: testEmailAddress });
      if (res?.success) setTestEmailStatus('success');
      else setTestEmailStatus('error');
    } catch {
      setTestEmailStatus('error');
    }
    setTimeout(() => setTestEmailStatus('idle'), 3000);
  };

  const filteredSubmissions = submissions.filter((s) => {
    const statusOk = statusFilter === 'all' ? true : s.status === statusFilter;
    const date = new Date(s.submitted_at).getTime();
    const startOk = startDate ? date >= new Date(startDate).getTime() : true;
    const endOk = endDate ? date <= new Date(endDate).getTime() : true;
    return statusOk && startOk && endOk;
  });

  const openDetail = (s: ContactSubmission) => {
    setDetail(s);
    setIsDetailOpen(true);
    if (s.status === 'new') {
      onUpdateStatus(s.id, 'read');
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    const ids = filteredSubmissions.map((s) => s.id);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : ids);
  };

  const toCSV = (rows: ContactSubmission[]) => {
    const headers = ['id','name','email','phone','subject','subject_value','objective','message','form_data','status','submitted_at'];
    const escape = (val: unknown) => {
      const s = String(val ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ');
      return `"${s}"`;
    };
    const csv = [
      headers.join(','),
      ...rows.map((r) => [
        escape(r.id),
        escape(r.name),
        escape(r.email),
        escape(r.phone || ''),
        escape(r.subject || ''),
        escape(r.subject_value || ''),
        escape(r.objective || ''),
        escape(r.message),
        escape(JSON.stringify(r.form_data || {})),
        escape(r.status),
        escape(r.submitted_at),
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact_submissions_${new Date().toISOString().slice(0,19)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCSVSingle = (s: ContactSubmission) => {
    toCSV([s]);
  };

  const exportCSVSelected = () => {
    const rows = filteredSubmissions.filter((s) => selectedIds.includes(s.id));
    if (rows.length) toCSV(rows);
  };

  const handleBulkDelete = () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    onBulkDelete(selectedIds);
    setSelectedIds([]);
  };

  const buildPDFHtml = (rows: ContactSubmission[]) => {
    const styles = `
      <style>
        body { font-family: Arial, sans-serif; color: #111; padding: 24px; }
        .header { margin-bottom: 16px; }
        .card { border: 1px solid #ddd; border-radius: 8px; padding: 16px; margin-bottom: 12px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .label { font-weight: 600; }
        .message { white-space: pre-wrap; border-top: 1px solid #eee; margin-top: 8px; padding-top: 8px; }
      </style>
    `;
    const content = rows.map((r) => `
      <div class="card">
        <div class="grid">
          <div><span class="label">Name:</span> ${r.name}</div>
          <div><span class="label">Email:</span> ${r.email}</div>
          <div><span class="label">Phone:</span> ${r.phone ?? ''}</div>
          <div><span class="label">Subject:</span> ${r.subject_value || r.subject || ''}</div>
          <div><span class="label">Status:</span> ${r.status}</div>
          <div><span class="label">Submitted:</span> ${new Date(r.submitted_at).toLocaleString()}</div>
        </div>
        ${r.form_data ? `
          <div style="margin-top: 12px; border-top: 1px solid #eee; padding-top: 8px;">
            <div class="label" style="margin-bottom: 4px;">Custom Fields:</div>
            <div class="grid">
              ${Object.entries(r.form_data).map(([k, v]) => `<div><span class="label">${k}:</span> ${v}</div>`).join('')}
            </div>
          </div>
        ` : ''}
        <div class="message"><span class="label">Message:</span><br>${(r.message || '').replace(/\n/g,'<br/>')}</div>
      </div>
    `).join('');
    return `<!DOCTYPE html><html><head><meta charset="utf-8">${styles}</head><body>
      <div class="header"><h2>Contact Submissions</h2></div>
      ${content}
    </body></html>`;
  };

  const exportPDFSingle = (s: ContactSubmission) => {
    const html = buildPDFHtml([s]);
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.document.title = `contact_${s.id}.pdf`;
    w.focus();
    w.print();
  };

  const exportPDFSelected = () => {
    const rows = filteredSubmissions.filter((s) => selectedIds.includes(s.id));
    if (!rows.length) return;
    const html = buildPDFHtml(rows);
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.document.title = `contact_bulk_${new Date().toISOString().slice(0,10)}.pdf`;
    w.focus();
    w.print();
  };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-display font-bold">Contact Management</h2>
        <div className="flex bg-[#0b0c15]/50 p-1 rounded-xl border border-white/10">
          {(['submissions', 'fields', 'settings', 'smtp'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab ? 'bg-white/10 text-white shadow-lg' : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'submissions' && (
        <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-4 flex flex-wrap gap-3 items-end border-b border-white/10">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              {selectedIds.length > 0 && onBulkDelete && (
                <button 
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-sm transition-colors"
                >
                  <Trash2 size={16} /> Delete ({selectedIds.length})
                </button>
              )}
              <button onClick={exportCSVSelected} className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm">
                <Download size={16} /> Export CSV
              </button>
              <button onClick={exportPDFSelected} className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm">
                <FileText size={16} /> Export PDF
              </button>
              <span className="text-xs text-gray-400 ml-2">{selectedIds.length} selected</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white/5 border-b border-white/10 text-gray-400 text-sm">
                  <th className="p-4">
                    <input type="checkbox" checked={filteredSubmissions.length > 0 && filteredSubmissions.every((s) => selectedIds.includes(s.id))} onChange={toggleSelectAll} />
                  </th>
                  <th className="p-4 font-medium">Name</th>
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Date</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredSubmissions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-white/5 transition-colors cursor-pointer" onClick={() => openDetail(sub)}>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(sub.id)} onChange={() => toggleSelect(sub.id)} />
                    </td>
                    <td className="p-4 font-medium">{sub.name}</td>
                    <td className="p-4 text-gray-400">{sub.email}</td>
                    <td className="p-4 text-gray-400 text-sm">
                      {new Date(sub.submitted_at).toLocaleDateString()}
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={sub.status}
                        onChange={(e) => onUpdateStatus(sub.id, e.target.value as any)}
                        className={`bg-transparent border-none text-sm font-medium focus:ring-0 cursor-pointer ${
                          sub.status === 'new' ? 'text-cyan-400' :
                          sub.status === 'read' ? 'text-purple-400' :
                          sub.status === 'replied' ? 'text-green-400' : 'text-gray-400'
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                        <option value="archived">Archived</option>
                      </select>
                    </td>
                    <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => openDetail(sub)}
                          className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10 rounded-lg transition-colors"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => exportCSVSingle(sub)}
                          className="p-2 text-gray-400 hover:text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => exportPDFSingle(sub)}
                          className="p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-400/10 rounded-lg transition-colors"
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          onClick={() => onDelete(sub.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {submissions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-500">
                      No submissions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'fields' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">Form Fields</h3>
            <div className="flex gap-3">
              <button
                onClick={handleCreateAtoZFields}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors"
              >
                <Plus size={18} /> Add A-Z Starter Fields
              </button>
              <button
                onClick={() => { 
                  setEditingField({
                    field_type: 'text',
                    width: 'full',
                    is_required: false,
                    is_active: true,
                    display_order: 0
                  }); 
                  setIsFieldModalOpen(true); 
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-lg hover:opacity-90 transition-opacity"
              >
                <Plus size={18} /> Add Field
              </button>
            </div>
          </div>
          <div className="grid gap-4">
            <Reorder.Group axis="y" values={fields} onReorder={handleReorder} className="space-y-4">
              {fields.map((field) => (
                <Reorder.Item 
                  key={field.id} 
                  value={field} 
                  className="bg-[#0b0c15]/50 border border-white/10 rounded-xl p-4 flex items-center justify-between cursor-move active:cursor-grabbing"
                  whileDrag={{ scale: 1.02, backgroundColor: "rgba(11, 12, 21, 0.8)", borderColor: "rgba(255, 255, 255, 0.2)" }}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-gray-500 cursor-grab active:cursor-grabbing">
                      <GripVertical size={20} />
                    </div>
                    <div>
                      <div className="font-medium">{field.field_label}</div>
                      <div className="text-sm text-gray-400 flex gap-4 mt-1">
                        <span>Key: {field.field_key}</span>
                        <span>Type: {field.field_type}</span>
                        {field.is_required && <span className="text-red-400">Required</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2" onPointerDown={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => { setEditingField(field); setIsFieldModalOpen(true); }}
                      className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10 rounded-lg transition-colors"
                    >
                      <Edit3 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.id)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          </div>
        </div>
      )}
      {isDetailOpen && detail && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0b0c15] border border-white/10 rounded-2xl p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Submission Details</h3>
              <button onClick={() => setIsDetailOpen(false)} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-400">Name</div>
                <div className="font-medium">{detail.name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Email</div>
                <div className="font-medium">{detail.email}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Phone</div>
                <div className="font-medium">{detail.phone || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Submitted</div>
                <div className="font-medium">{new Date(detail.submitted_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Status</div>
                <select
                  value={detail.status}
                  onChange={(e) => { onUpdateStatus(detail.id, e.target.value as any); setDetail({ ...detail, status: e.target.value as any }); }}
                  className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            {detail.form_data && Object.keys(detail.form_data).length > 0 && (
              <div className="mt-4">
                <div className="text-sm text-gray-400 mb-2">Custom Fields</div>
                <div className="bg-[#0b0c15]/50 border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(detail.form_data).map(([key, value]) => {
                    // Skip standard fields that are already displayed
                    if (['name', 'email', 'phone', 'subject', 'objective', 'message'].includes(key.toLowerCase())) return null;
                    return (
                      <div key={key}>
                        <div className="text-sm text-gray-400 capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="font-medium text-gray-200">{String(value)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="text-sm text-gray-400 mb-1">Message</div>
              <div className="bg-[#0b0c15]/50 border border-white/10 rounded-xl p-4 text-gray-200 whitespace-pre-wrap">{detail.message}</div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => exportCSVSingle(detail)} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg">
                <Download size={16} /> Export CSV
              </button>
              <button onClick={() => exportPDFSingle(detail)} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg">
                <FileText size={16} /> Export PDF
              </button>
              <button onClick={() => setIsDetailOpen(false)} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg">
                <X size={16} /> Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {activeTab === 'settings' && settings && (
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-2">Top Section Content</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Top Heading</label>
                    <input value={settings.section_title || ''} onChange={(e) => setSettings({...settings, section_title: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="Contact Us" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Top Subtitle</label>
                    <input value={settings.section_subtitle || ''} onChange={(e) => setSettings({...settings, section_subtitle: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="Let us know your requirements" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Top Description</label>
                    <textarea value={settings.section_description || ''} onChange={(e) => setSettings({...settings, section_description: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white h-24" placeholder="Share details and we will get back to you shortly." />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Submit Button Text</label>
                    <input value={settings.submit_button_text || ''} onChange={(e) => setSettings({...settings, submit_button_text: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-4">Messages</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Success Message</label>
                    <input value={settings.success_message || ''} onChange={(e) => setSettings({...settings, success_message: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Error Message</label>
                    <input value={settings.error_message || ''} onChange={(e) => setSettings({...settings, error_message: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">

              <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-2">Left Side Contact Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Left Badge Text</label>
                    <input value={settings.details_badge_text || ''} onChange={(e) => setSettings({...settings, details_badge_text: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="Contact Details" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Left Heading</label>
                    <input value={settings.details_heading || ''} onChange={(e) => setSettings({...settings, details_heading: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="Talk To Our Team" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Left Description</label>
                    <textarea value={settings.details_description || ''} onChange={(e) => setSettings({...settings, details_description: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white h-24" placeholder="Share your requirements through the form, or reach out directly using the details below." />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                      <input type="checkbox" checked={settings.show_email} onChange={(e) => setSettings({...settings, show_email: e.target.checked})} /> Show Email
                    </label>
                    {settings.show_email && (
                    <input value={settings.email_recipient || ''} onChange={(e) => setSettings({...settings, email_recipient: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="Display Email Address" />
                    )}
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                      <input type="checkbox" checked={settings.show_phone} onChange={(e) => setSettings({...settings, show_phone: e.target.checked})} /> Show Phone
                    </label>
                    {settings.show_phone && (
                      <input value={settings.phone_number || ''} onChange={(e) => setSettings({...settings, phone_number: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="Phone Number" />
                    )}
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                      WhatsApp Number (for header icon)
                    </label>
                    <input value={settings.whatsapp_number || ''} onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="1234567890 (with country code, no + or spaces)" />
                    <p className="text-xs text-gray-500 mt-1">This number will be used for the WhatsApp icon in the header</p>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                      <input type="checkbox" checked={settings.show_address} onChange={(e) => setSettings({...settings, show_address: e.target.checked})} /> Show Address
                    </label>
                    {settings.show_address && (
                      <textarea value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white h-20" placeholder="Address" />
                    )}
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                      <input type="checkbox" checked={settings.show_social} onChange={(e) => setSettings({...settings, show_social: e.target.checked})} /> Show Social Links
                    </label>
                    {settings.show_social && (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500">Add social platform names and URLs.</p>
                        <div className="space-y-3">
                          {socialRows.map((item, index) => (
                            <div key={`${item.platform}-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr_auto] gap-2">
                              <input
                                value={item.platform}
                                onChange={(e) => updateSocialRow(index, 'platform', e.target.value)}
                                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                                placeholder="Platform"
                              />
                              <input
                                value={item.url}
                                onChange={(e) => updateSocialRow(index, 'url', e.target.value)}
                                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                                placeholder="https://example.com/profile"
                              />
                              <button
                                type="button"
                                onClick={() => removeSocialRow(index)}
                                className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={addSocialRow}
                          className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm"
                        >
                          <Plus size={14} />
                          Add Social Link
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
          <div className="flex justify-end pt-6 border-t border-white/10">
            <button onClick={handleSaveSettings} disabled={isSaving} className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-2 disabled:opacity-50">
              <Save size={18} />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'smtp' && settings && (
        <div className="max-w-2xl mx-auto space-y-8">
           <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
             <h3 className="text-xl font-bold mb-6">SMTP Configuration</h3>
             <div className="space-y-4">
               <div>
                 <label className="block text-sm text-gray-400 mb-1">SMTP Host</label>
                 <input value={settings.smtp_host || ''} onChange={(e) => setSettings({...settings, smtp_host: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="smtp.example.com" />
               </div>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm text-gray-400 mb-1">Port</label>
                   <input type="number" value={settings.smtp_port || 587} onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value)})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
                 </div>
                 <div className="flex items-end mb-2">
                   <label className="flex items-center gap-2 text-sm text-gray-300">
                     <input type="checkbox" checked={settings.smtp_secure || false} onChange={(e) => setSettings({...settings, smtp_secure: e.target.checked})} /> Use Secure Connection (SSL/TLS)
                   </label>
                 </div>
               </div>
               <div>
                 <label className="block text-sm text-gray-400 mb-1">Username</label>
                 <input value={settings.smtp_user || ''} onChange={(e) => setSettings({...settings, smtp_user: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="user@example.com" />
               </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Password</label>
                <input type="password" value={settings.smtp_pass || ''} onChange={(e) => setSettings({...settings, smtp_pass: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="••••••••" />
                {settings.has_smtp_pass && !settings.smtp_pass && (
                  <p className="text-xs text-green-400 mt-1">SMTP password already saved. Leave blank to keep current password.</p>
                )}
              </div>
               <div>
                 <label className="block text-sm text-gray-400 mb-1">Notification Recipient</label>
                 <input value={settings.email_recipient} onChange={(e) => setSettings({...settings, email_recipient: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" placeholder="where.to.send.notifications@example.com" />
               </div>
             </div>
             <div className="mt-6 flex justify-end">
               <button onClick={handleSaveSettings} disabled={isSaving} className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-2 disabled:opacity-50">
                 <Save size={18} />
                 {isSaving ? 'Saving...' : 'Save Configuration'}
               </button>
             </div>
           </div>

           <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
             <h3 className="text-xl font-bold mb-6">Email Template</h3>
             <div className="space-y-4">
               <div>
                 <label className="block text-sm text-gray-400 mb-1">Email Subject</label>
                 <input value={settings.email_subject} onChange={(e) => setSettings({...settings, email_subject: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
               </div>
               <div>
                 <label className="block text-sm text-gray-400 mb-1">Email Body Template (HTML)</label>
                 <p className="text-xs text-gray-500 mb-2">Use placeholders like &#123;name&#125;, &#123;email&#125;, &#123;message&#125; to insert submission data.</p>
                 <div className="bg-[#1a1a2e] rounded-lg overflow-hidden">
                   <ReactQuill 
                     theme="snow"
                     value={settings.email_template || ''}
                     onChange={(content) => setSettings({...settings, email_template: content})}
                     className="text-white"
                     modules={{
                       toolbar: [
                         [{ 'header': [1, 2, 3, false] }],
                         ['bold', 'italic', 'underline', 'strike'],
                         [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                         ['link', 'clean']
                       ]
                     }}
                   />
                 </div>
               </div>
               <div className="flex justify-end pt-4">
                 <button onClick={handleSaveSettings} disabled={isSaving} className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-2 disabled:opacity-50">
                   <Save size={18} />
                   Save Template
                 </button>
               </div>
             </div>
           </div>

           <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
             <h3 className="text-xl font-bold mb-4">Test Configuration</h3>
             <p className="text-gray-400 mb-4 text-sm">Send a test email to verify your SMTP settings are working correctly.</p>
             <div className="flex gap-2">
               <input value={testEmailAddress} onChange={(e) => setTestEmailAddress(e.target.value)} className="flex-1 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 h-16 text-white" placeholder="Enter recipient email" />
               <button onClick={handleTestEmail} disabled={testEmailStatus === 'sending'} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg flex items-center gap-2 disabled:opacity-50">
                 {testEmailStatus === 'sending' ? <RefreshCw size={18} className="animate-spin" /> : <Mail size={18} />}
                 Send Test
               </button>
             </div>
           </div>
        </div>
      )}

      {/* Field Edit Modal */}
      {isFieldModalOpen && editingField && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0b0c15] border border-white/10 rounded-2xl p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-6">{editingField.id ? 'Edit Field' : 'Add New Field'}</h3>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Label</label>
                  <input 
                    value={editingField.field_label || ''} 
                    onChange={(e) => {
                      const label = e.target.value;
                      const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
                      setEditingField((prev: any) => ({
                        ...prev, 
                        field_label: label,
                        field_key: !prev.id ? slug : prev.field_key
                      }));
                    }} 
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Type</label>
                  <select value={editingField.field_type || 'text'} onChange={(e) => setEditingField({...editingField, field_type: e.target.value as any})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white">
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="tel">Telephone</option>
                    <option value="textarea">Textarea</option>
                    <option value="select">Select</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="radio">Radio</option>
                    <option value="url">URL</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="time">Time</option>
                    <option value="datetime-local">Date & Time</option>
                    <option value="password">Password</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Placeholder</label>
                <input value={editingField.placeholder || ''} onChange={(e) => setEditingField({...editingField, placeholder: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm text-gray-400 mb-1">Width</label>
                   <select value={editingField.width || 'full'} onChange={(e) => setEditingField({...editingField, width: e.target.value as any})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white">
                     <option value="full">Full Width</option>
                     <option value="half">Half Width</option>
                     <option value="third">One Third</option>
                   </select>
                 </div>
                 <div className="flex items-end mb-2">
                   <label className="flex items-center gap-2 text-sm text-gray-300">
                     <input type="checkbox" checked={editingField.is_required || false} onChange={(e) => setEditingField({...editingField, is_required: e.target.checked})} /> Required Field
                   </label>
                 </div>
              </div>
              {(editingField.field_type === 'select' || editingField.field_type === 'radio') && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Options (comma separated)</label>
                  <textarea value={editingField.options || ''} onChange={(e) => setEditingField({...editingField, options: e.target.value})} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white h-20" placeholder="Option 1, Option 2, Option 3" />
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleSaveField} className="flex-1 bg-green-600 hover:bg-green-700 py-2 rounded-lg text-white font-medium">Save Field</button>
              <button onClick={() => setIsFieldModalOpen(false)} className="flex-1 bg-white/10 hover:bg-white/20 py-2 rounded-lg text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ContactManager;
